import {
    Component,
    OnInit,
    OnDestroy,
    ViewChild,
    ElementRef,
    AfterViewInit,
    Inject,
} from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";

declare let google: any;

interface PuntoGeolocalizacion {
    latitud: number;
    longitud: number;
    titulo: string;
    descripcion: string;
    tipo: "inicio" | "fin";
}

@Component({
    selector: "app-mapa-geolocalizacion",
    templateUrl: "./mapa-geolocalizacion.component.html",
    styleUrls: ["./mapa-geolocalizacion.component.scss"],
    standalone: false
})
export class MapaGeolocalizacionComponent
    implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild("mapContainer", { static: false }) mapContainer: ElementRef;

    private map: any;
    private markers: any[] = [];
    private infoWindow: any;
    public googleMapsLoaded = false;

    puntos: PuntoGeolocalizacion[] = [];
    registro: any;

    constructor(
        public dialogRef: MatDialogRef<MapaGeolocalizacionComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {
        this.registro = data.registro;
    }

    ngOnInit() {
        this.prepararPuntosGeolocalizacion();
        this.cargarGoogleMaps();
    }

    ngAfterViewInit() {
        if (this.googleMapsLoaded) {
            this.inicializarMapa();
        }
    }

    ngOnDestroy() {
        if (this.map) {
            // Limpiar el mapa
            this.markers.forEach((marker) => {
                if (marker) {
                    marker.setMap(null);
                }
            });
        }
    }

    private prepararPuntosGeolocalizacion() {
        this.puntos = [];

        // Punto de inicio
        if (this.registro.latitudInicio && this.registro.longitudInicio) {
            this.puntos.push({
                latitud: this.registro.latitudInicio,
                longitud: this.registro.longitudInicio,
                titulo: "Inicio del turno",
                descripcion: `Hora de entrada: ${this.registro.horaEntrada}`,
                tipo: "inicio",
            });
        }

        // Punto de fin
        if (this.registro.latitudFin && this.registro.longitudFin) {
            this.puntos.push({
                latitud: this.registro.latitudFin,
                longitud: this.registro.longitudFin,
                titulo: "Fin del turno",
                descripcion: `Hora de salida: ${this.registro.horaSalida}`,
                tipo: "fin",
            });
        }
    }

    private cargarGoogleMaps() {
        if (typeof google !== "undefined" && google.maps) {
            this.googleMapsLoaded = true;
            this.inicializarMapa();
            return;
        }

        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDhkUMYHoYJCyD9rY-HDxSTpf6k5ttYThA&libraries=geometry`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
            this.googleMapsLoaded = true;
            this.inicializarMapa();
        };
        document.head.appendChild(script);
    }

    private inicializarMapa() {
        if (
            !this.mapContainer ||
            !this.googleMapsLoaded ||
            this.puntos.length === 0
        ) {
            return;
        }

        // Calcular el centro del mapa basado en los puntos disponibles
        const bounds = new google.maps.LatLngBounds();
        this.puntos.forEach((punto) => {
            bounds.extend(
                new google.maps.LatLng(punto.latitud, punto.longitud)
            );
        });

        // Configuración del mapa
        const mapOptions = {
            zoom: 15,
            center: bounds.getCenter(),
            mapTypeId: google.maps.MapTypeId.ROADMAP,
            mapTypeControl: true,
            streetViewControl: true,
            fullscreenControl: true,
            zoomControl: true,
            zoomControlOptions: {
                position: google.maps.ControlPosition.RIGHT_CENTER,
            },
        };

        // Crear el mapa
        this.map = new google.maps.Map(
            this.mapContainer.nativeElement,
            mapOptions
        );

        // Crear ventana de información
        this.infoWindow = new google.maps.InfoWindow();

        // Agregar marcadores
        this.agregarMarcadores();

        // Usar siempre el zoom especificado
        this.map.setZoom(15);
    }

    private agregarMarcadores() {
        this.puntos.forEach((punto, index) => {
            const marker = new google.maps.Marker({
                position: new google.maps.LatLng(punto.latitud, punto.longitud),
                map: this.map,
                title: punto.titulo,
                icon: this.obtenerIconoMarcador(punto.tipo),
                animation: google.maps.Animation.DROP,
            });

            // Contenido de la ventana de información
            const contentString = `
        <div style="padding: 10px;">
          <h3 style="margin: 0 0 10px 0; color: #d48f00;">${punto.titulo}</h3>
          <p style="margin: 5px 0;"><strong>${punto.descripcion}</strong></p>
          <p style="margin: 5px 0; font-size: 12px; color: #666;">
            Coordenadas: ${punto.latitud.toFixed(6)}, ${punto.longitud.toFixed(
                6
            )}
          </p>
        </div>
      `;

            // Evento click del marcador
            marker.addListener("click", () => {
                this.infoWindow.setContent(contentString);
                this.infoWindow.open(this.map, marker);
            });

            this.markers.push(marker);
        });

        // Si hay dos puntos, dibujar una línea entre ellos
        if (this.puntos.length === 2) {
            this.dibujarRuta();
        }
    }

    private obtenerIconoMarcador(tipo: "inicio" | "fin"): any {
        const iconConfig = {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: tipo === "inicio" ? "#4CAF50" : "#F44336",
            fillOpacity: 1,
            strokeColor: "#FFFFFF",
            strokeWeight: 2,
        };

        return iconConfig;
    }

    private dibujarRuta() {
        const ruta = new google.maps.Polyline({
            path: [
                new google.maps.LatLng(
                    this.puntos[0].latitud,
                    this.puntos[0].longitud
                ),
                new google.maps.LatLng(
                    this.puntos[1].latitud,
                    this.puntos[1].longitud
                ),
            ],
            geodesic: true,
            strokeColor: "#d48f00",
            strokeOpacity: 0.8,
            strokeWeight: 3,
            map: this.map,
        });

        // Calcular distancia
        const distancia = google.maps.geometry.spherical.computeDistanceBetween(
            new google.maps.LatLng(
                this.puntos[0].latitud,
                this.puntos[0].longitud
            ),
            new google.maps.LatLng(
                this.puntos[1].latitud,
                this.puntos[1].longitud
            )
        );

        // Agregar información de distancia
        const distanciaKm = (distancia / 1000).toFixed(2);
        const infoDistancia = `
            <div style="padding: 10px; background: #f5f5f5; border-radius: 4px; margin-top: 10px;">
                <p style="margin: 0; font-size: 12px; color: #666;">
                    <strong>Distancia entre puntos:</strong> ${distanciaKm} km
                </p>
            </div>
        `;

        // Agregar marcador de distancia en el centro de la ruta
        const centroRuta = new google.maps.LatLng(
            (this.puntos[0].latitud + this.puntos[1].latitud) / 2,
            (this.puntos[0].longitud + this.puntos[1].longitud) / 2
        );

        const markerDistancia = new google.maps.Marker({
            position: centroRuta,
            map: this.map,
            icon: {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 6,
                fillColor: "#d48f00",
                fillOpacity: 0.8,
                strokeColor: "#FFFFFF",
                strokeWeight: 2,
            },
        });

        markerDistancia.addListener("click", () => {
            this.infoWindow.setContent(infoDistancia);
            this.infoWindow.open(this.map, markerDistancia);
        });

        this.markers.push(markerDistancia);
    }

    cerrar() {
        this.dialogRef.close();
    }
  public trackByIndex(i: number) { return i; }

}
